import React from 'react'

import { INVOICE_PERIOD_OPTIONS } from '../../lib/invoicePeriodOptions'
import { formatDateShort } from '../../lib/dates'
import { useAuth } from '../../context/auth'
import Button, { ButtonSize, ButtonVariant } from '../ui/button'
import ButtonLink from '../ui/button/link'
import FieldLabel from '../ui/input/FieldLabel'
import Input from '../ui/input'
import SelectBox from '../ui/selectBox'

import './DateRangeFilter.scss'

export type DateRangeValue = {
  period?: string
  from?: string
  to?: string
}

type Props = {
  value: DateRangeValue
  onChange: (value: DateRangeValue) => void
}

const TRIGGER_VALUE = 'current'

const PERIOD_OPTIONS = INVOICE_PERIOD_OPTIONS.map((opt) => ({
  label: opt.label,
  value: opt.value,
}))

const getTriggerLabel = (value: DateRangeValue, locale?: string): string => {
  if (value.from && value.to) {
    return `${formatDateShort(value.from, locale)} – ${formatDateShort(value.to, locale)}`
  }
  if (value.period && value.period !== 'all') {
    const option = INVOICE_PERIOD_OPTIONS.find((opt) => opt.value === value.period)
    return option?.label ?? 'All'
  }
  return 'All'
}

const hasActiveFilter = (value: DateRangeValue): boolean =>
  Boolean((value.from && value.to) || (value.period && value.period !== 'all'))

const DateRangeFilter: React.FC<Props> = ({ value, onChange }) => {
  const { user } = useAuth()
  const locale = user?.locale
  const [open, setOpen] = React.useState(false)
  const [draftPeriod, setDraftPeriod] = React.useState('all')
  const [draftFrom, setDraftFrom] = React.useState('')
  const [draftTo, setDraftTo] = React.useState('')

  const triggerLabel = getTriggerLabel(value, locale)
  const triggerOptions = React.useMemo(
    () => [{ label: triggerLabel, value: TRIGGER_VALUE }],
    [triggerLabel]
  )

  const syncDraftFromValue = React.useCallback(() => {
    setDraftPeriod(value.period ?? 'all')
    setDraftFrom(value.from ?? '')
    setDraftTo(value.to ?? '')
  }, [value])

  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      // react-select menus may render outside the panel; keep panel open while interacting.
      if ((target as Element).closest?.('[role="listbox"]')) return
      setOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const openPopover = () => {
    syncDraftFromValue()
    setOpen(true)
  }

  const togglePopover = () => {
    if (open) {
      setOpen(false)
    } else {
      openPopover()
    }
  }

  const handlePresetChange = (event: { target: { value: string } }) => {
    setDraftPeriod(event.target.value)
    setDraftFrom('')
    setDraftTo('')
  }

  const handleFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftFrom(event.target.value)
    setDraftPeriod('')
  }

  const handleToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftTo(event.target.value)
    setDraftPeriod('')
  }

  const isCustomDraft = draftFrom !== '' || draftTo !== ''
  const hasInvalidRange = isCustomDraft && draftFrom !== '' && draftTo !== '' && draftFrom > draftTo
  const canApply = isCustomDraft
    ? draftFrom !== '' && draftTo !== '' && !hasInvalidRange
    : true

  const handleApply = () => {
    if (!canApply) return
    if (isCustomDraft && draftFrom && draftTo) {
      onChange({ from: draftFrom, to: draftTo })
    } else if (draftPeriod && draftPeriod !== 'all') {
      onChange({ period: draftPeriod })
    } else {
      onChange({})
    }
    setOpen(false)
  }

  const handleClear = () => {
    onChange({})
    setOpen(false)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const hasDraftPreset = Boolean(draftPeriod && draftPeriod !== 'all')

  return (
    <div className='date-range-filter' ref={containerRef}>
      <FieldLabel label='Date Range' htmlFor='date-range-filter-trigger' />
      <div
        className='bg-white date-range-filter__trigger-wrap'
        onClick={togglePopover}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            togglePopover()
          }
        }}
        role='button'
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup='dialog'
      >
        <SelectBox
          key={triggerLabel}
          id='date-range-filter-trigger'
          name='dateRangeTrigger'
          size='sm'
          placeHolder=''
          options={triggerOptions}
          value={TRIGGER_VALUE}
          onChange={() => {}}
          noBorder={hasActiveFilter(value)}
          menuIsOpen={false}
          openMenuOnClick={false}
          openMenuOnFocus={false}
          isSearchable={false}
          tabSelectsValue={false}
        />
      </div>
      {open && (
        <div className='date-range-filter__panel' role='dialog' aria-label='Filter by date range'>
          <FieldLabel label='Period' htmlFor='date-range-filter-preset' />
          <div className='bg-white'>
            <SelectBox
              id='date-range-filter-preset'
              name='period'
              size='sm'
              placeHolder=''
              options={PERIOD_OPTIONS}
              value={draftPeriod || 'all'}
              onChange={handlePresetChange}
              noBorder={hasDraftPreset || isCustomDraft}
            />
          </div>

          <div className='date-range-filter__separator'>
            <span>or</span>
          </div>

          <div className='date-range-filter__custom'>
            <Input
              label='From'
              type='date'
              id='date-range-filter-from'
              name='from'
              value={draftFrom}
              onChange={handleFromChange}
            />
            <Input
              label='To'
              type='date'
              id='date-range-filter-to'
              name='to'
              value={draftTo}
              onChange={handleToChange}
            />
          </div>

          {hasInvalidRange && (
            <p className='date-range-filter__error'>"From" date must be before or equal to "To" date.</p>
          )}

          <div className='date-range-filter__actions'>
            <ButtonLink type='button' onClick={handleClose}>
              Close
            </ButtonLink>
            <div className='date-range-filter__actions-buttons'>
              <Button type='button' variant={ButtonVariant.LIGHT} size={ButtonSize.SMALL} onClick={handleClear}>
                Clear
              </Button>
              <Button
                type='button'
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SMALL}
                onClick={handleApply}
                disabled={!canApply}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DateRangeFilter
